import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, ShoppingBag, Star, Trash2, RefreshCw } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useWishlist } from '@/context/WishlistContext';
import { useCart } from '@/context/CartContext';
import { cn } from '@/lib/utils';

const WishlistPage = () => {
  const navigate = useNavigate();
  const { wishlist, loading, fetchWishlist, removeFromWishlist, wishlistCount } = useWishlist();
  const { addToCart } = useCart();
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWishlist();
    setRefreshing(false);
  };

  const handleProductPress = (item: any) => {
    navigate(`/product/${item.productId}`);
  };

  const handleRemove = async (productId: number, productName: string) => {
    if (window.confirm(`Remove "${productName}" from your wishlist?`)) {
      await removeFromWishlist(productId);
    }
  };

  const handleAddToCart = async (item: any) => {
    try {
      const cartItem = {
        productId: item.productId,
        productName: item.productName,
        currentPrice: item.minPrice,
        originalPrice: item.maxPrice,
        primaryImageUrl: item.imageUrl,
        quantity: 1,
        seller: 'AuraKart', // Required field
      };

      await addToCart(cartItem);
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const WishlistItem = ({ item, index }: { item: any; index: number }) => {
    const minPrice = item.minPrice ?? 0;
    const maxPrice = item.maxPrice ?? null;
    const rating = Number(item.rating ?? 0);
    const hasDiscount = maxPrice && maxPrice > minPrice;
    const discountPercentage = hasDiscount ? Math.round(((maxPrice - minPrice) / maxPrice) * 100) : 0;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
        className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
      >
        <div 
          className="cursor-pointer"
          onClick={() => handleProductPress(item)}
        >
          <div className="relative aspect-square">
            <img
              src={item.imageUrl || 'https://placehold.co/300x360?text=No+Image'}
              alt={item.productName}
              className="w-full h-full object-cover"
            />
            
            {/* NEW Badge */}
            <div className="absolute top-2 left-2">
              <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                NEW
              </Badge>
            </div>

            {/* Filled Heart */}
            <div className="absolute top-2 right-2">
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <Heart className="h-4 w-4 text-white fill-current" />
              </div>
            </div>

            {/* Rating */}
            {rating > 0 && (
              <div className="absolute bottom-2 left-2 bg-black/70 text-white px-2 py-1 rounded-md text-xs flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                <span>{rating.toFixed(1)}</span>
              </div>
            )}
          </div>

          <div className="p-4">
            <h3 className="font-medium text-sm text-gray-900 line-clamp-2 mb-1">
              {item.productName || 'Unnamed Product'}
            </h3>
            
            <p className="text-xs text-gray-500 mb-2">
              {item.brand || 'AuraKart'}
            </p>

            {/* Price with discount */}
            <div className="flex items-center gap-2 mb-3">
              <span className="font-bold text-lg text-gray-900">
                ₹{minPrice > 0 ? minPrice.toFixed(0) : '—'}
              </span>
              
              {hasDiscount && (
                <>
                  <span className="text-sm text-gray-500 line-through">
                    ₹{maxPrice.toFixed(0)}
                  </span>
                  <Badge variant="destructive" className="text-xs">
                    {discountPercentage}% off
                  </Badge>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-4 pt-0 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              handleRemove(item.productId, item.productName || 'this item');
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Remove
          </Button>
          <Button
            variant="default"
            size="sm"
            className="flex-1"
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart(item);
            }}
          >
            Add to Cart
          </Button>
        </div>
      </motion.div>
    );
  };

  const EmptyWishlist = () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-secondary flex items-center justify-center">
        <Heart className="h-10 w-10 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-bold mb-4">Your Wishlist is Empty</h1>
      <p className="text-muted-foreground mb-8">
        Save your favorite items by tapping the heart
      </p>
      <Link to="/">
        <Button variant="default" className="bg-primary hover:bg-primary/90">
          Start Shopping
        </Button>
      </Link>
    </div>
  );

  if (loading && wishlist.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-4 animate-spin">
            <RefreshCw className="w-16 h-16 text-primary" />
          </div>
          <p className="text-muted-foreground">Loading your wishlist...</p>
        </div>
      </Layout>
    );
  }

  if (wishlist.length === 0) {
    return (
      <Layout>
        <EmptyWishlist />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">My Wishlist ({wishlistCount})</h1>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className={cn(refreshing && "opacity-50")}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {wishlist.map((item, index) => (
            <WishlistItem key={`wishlist-${item.wishlistId || item.productId}`} item={item} index={index} />
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default WishlistPage;
